import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, QrCode, Plus, Loader2, Trash2, ShieldCheck, CalendarClock, Wrench, Copy, Download, Printer,
} from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { MaintenancePill } from "./StudioSite";
import { studio, API, apiError, beDate, inputCls, btnPrimary, btnGhost, authHeaders } from "@/lib/studioApi";
import { Card, SectionCard, EmptyState, Skeleton } from "@/components/studio/StudioUI";

const today = () => new Date().toISOString().slice(0, 10);

export default function StudioEquipment() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [qr, setQr] = useState("");
  const [entry, setEntry] = useState({ date: today(), type: "entretien", performed_by: "", notes: "", cost: "" });
  const [saving, setSaving] = useState(false);

  const load = () =>
    studio.get(`/equipments/${id}`).then(({ data: d }) => setData(d)).catch(() => navigate("/studio/batiments"));

  useEffect(() => {
    load();
    studio.get("/assets/options").then(({ data: d }) => setOptions(d)).catch(() => {});
    fetch(`${API}/studio/equipments/${id}/qr`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => b && setQr(URL.createObjectURL(b)))
      .catch(() => {});
  }, [id]);

  const addEntry = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await studio.post(`/equipments/${id}/log`, entry);
      toast.success("Entretien enregistré — la prochaine échéance est recalculée");
      setEntry({ date: today(), type: "entretien", performed_by: "", notes: "", cost: "" });
      load();
    } catch (err) {
      toast.error(apiError(err, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (entryId) => {
    try {
      await studio.delete(`/equipments/${id}/log/${entryId}`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Suppression impossible"));
    }
  };

  const removeEquipment = async () => {
    if (!window.confirm("Supprimer cet équipement et son historique ?")) return;
    const siteId = data?.site?.id;
    await studio.delete(`/equipments/${id}`);
    toast.success("Équipement supprimé");
    navigate(siteId ? `/studio/batiments/${siteId}` : "/studio/batiments");
  };

  const eq = data?.equipment;

  return (
    <StudioLayout
      breadcrumb={data?.site ? `Bâtiments · ${data.site.name}` : "Bâtiments"}
      title={eq?.name || "Passeport technique"}
      subtitle={eq ? [eq.category_label, eq.brand, eq.model].filter(Boolean).join(" · ") : ""}
      action={
        data?.site && (
          <Link to={`/studio/batiments/${data.site.id}`} data-testid="back-to-site" className={`${btnGhost} hidden lg:inline-flex`}>
            <ArrowLeft className="h-4 w-4" /> Retour au bâtiment
          </Link>
        )
      }
    >
      {!data ? (
        <div className="mt-6 space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="space-y-4 lg:col-span-3">
            <Card data-testid="equipment-summary" className="overflow-hidden">
              <div className="flex flex-wrap items-center gap-2 border-b border-[#E2E7F7] bg-[#F6F8FD]/70 px-4 py-3 sm:px-5">
                <MaintenancePill state={eq.maintenance_state} testId="equipment-state" />
                {eq.under_warranty && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                    <ShieldCheck className="h-3 w-3" /> Sous garantie jusqu'au {beDate(eq.warranty_until)}
                  </span>
                )}
                {eq.next_maintenance && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-[#E2E7F7] bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#5C6A87]">
                    <CalendarClock className="h-3 w-3" /> Prochain entretien : {beDate(eq.next_maintenance)}
                  </span>
                )}
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 py-4 sm:px-5">
                {[
                  ["Catégorie", eq.category_label],
                  ["Marque", eq.brand],
                  ["Modèle", eq.model],
                  ["N° de série", eq.serial],
                  ["Puissance", eq.power_kw ? `${eq.power_kw} kW` : ""],
                  ["Emplacement", eq.location],
                  ["Installé le", eq.install_date ? beDate(eq.install_date) : ""],
                  ["Dernier entretien", eq.last_maintenance ? beDate(eq.last_maintenance) : ""],
                  ["Fréquence", eq.maintenance_interval_months ? `tous les ${eq.maintenance_interval_months} mois` : ""],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="min-w-0">
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-[#93A0BC]">{k}</dt>
                    <dd className="truncate text-sm font-semibold text-[#0F2645]">{v}</dd>
                  </div>
                ))}
              </dl>
              {eq.notes && <p className="border-t border-[#E2E7F7] px-4 py-3 text-xs leading-relaxed text-[#5C6A87] sm:px-5">{eq.notes}</p>}
            </Card>

            <SectionCard testId="equipment-log" title="Historique d'entretien" hint="Ajoutez chaque passage : la prochaine échéance se recalcule">
              <form onSubmit={addEntry} data-testid="log-form" className="mb-4 grid grid-cols-1 gap-2.5 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD]/60 p-3 sm:grid-cols-2">
                <input type="date" data-testid="log-date" value={entry.date} onChange={(e) => setEntry({ ...entry, date: e.target.value })} className={inputCls} required />
                <select data-testid="log-type" value={entry.type} onChange={(e) => setEntry({ ...entry, type: e.target.value })} className={inputCls}>
                  {(options?.log_types || []).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input data-testid="log-by" value={entry.performed_by} onChange={(e) => setEntry({ ...entry, performed_by: e.target.value })} placeholder="Réalisé par" className={inputCls} />
                <input data-testid="log-cost" value={entry.cost} onChange={(e) => setEntry({ ...entry, cost: e.target.value })} placeholder="Coût (€)" className={inputCls} />
                <textarea data-testid="log-notes" value={entry.notes} onChange={(e) => setEntry({ ...entry, notes: e.target.value })} placeholder="Ce qui a été fait, pièces remplacées, valeurs de combustion…" rows={2} className={`${inputCls} sm:col-span-2`} />
                <button type="submit" data-testid="log-submit" disabled={saving} className={`${btnPrimary} sm:col-span-2`}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Enregistrer dans l'historique
                </button>
              </form>

              {data.log.length === 0 ? (
                <EmptyState testId="log-empty" icon={Wrench} title="Aucune intervention enregistrée" hint="Le premier entretien enregistré déclenchera le rappel automatique à la date suivante." />
              ) : (
                <div className="space-y-2">
                  {data.log.map((l) => (
                    <div key={l.id} data-testid={`log-row-${l.id}`} className="flex min-h-[56px] items-start gap-3 rounded-xl border border-[#E2E7F7] px-3 py-2.5">
                      <span className="w-[74px] shrink-0 pt-0.5 text-xs font-bold tabular-nums text-[#5C6A87]">{beDate(l.date)}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-[#0F2645]">{l.type_label}</span>
                        {l.notes && <span className="mt-0.5 block text-xs leading-relaxed text-[#5C6A87]">{l.notes}</span>}
                        {(l.performed_by || l.cost) && (
                          <span className="mt-0.5 block text-[11px] text-[#93A0BC]">
                            {[l.performed_by, l.cost ? `${l.cost} €` : ""].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </span>
                      <button
                        type="button"
                        data-testid={`log-delete-${l.id}`}
                        onClick={() => removeEntry(l.id)}
                        className="shrink-0 rounded-lg p-2 text-slate-300 hover:bg-rose-50 hover:text-rose-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          <div className="space-y-4 lg:col-span-2">
            <SectionCard testId="equipment-qr" title="QR code de l'appareil" hint="À coller sur la chaudière : le client voit son historique et vos coordonnées">
              {qr ? (
                <div className="flex flex-col items-center gap-3">
                  <img src={qr} alt="QR code du passeport" data-testid="qr-image" className="h-44 w-44 rounded-xl border border-[#E2E7F7] bg-white p-2" />
                  <div className="flex w-full flex-wrap gap-2">
                    <a href={qr} download={`passeport-${eq.serial || eq.name}.png`} data-testid="qr-download" className={`${btnGhost} flex-1`}>
                      <Download className="h-3.5 w-3.5" /> Télécharger
                    </a>
                    <button type="button" data-testid="qr-print" onClick={() => window.print()} className={`${btnGhost} flex-1`}>
                      <Printer className="h-3.5 w-3.5" /> Imprimer
                    </button>
                  </div>
                  <button
                    type="button"
                    data-testid="qr-copy-link"
                    onClick={() => {
                      navigator.clipboard?.writeText(data.public_url);
                      toast.success("Lien du passeport copié");
                    }}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3A5CC8] hover:underline"
                  >
                    <Copy className="h-3 w-3" /> Copier le lien public
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-[#5C6A87]">
                  <QrCode className="h-4 w-4" /> Génération du QR code…
                </div>
              )}
            </SectionCard>

            {data.site && (
              <SectionCard testId="equipment-site" title="Bâtiment">
                <Link to={`/studio/batiments/${data.site.id}`} data-testid="equipment-site-link" className="block rounded-xl border border-[#E2E7F7] p-3 hover:border-[#3A5CC8]/40">
                  <p className="truncate text-sm font-bold text-[#0F2645]">{data.site.name}</p>
                  <p className="mt-0.5 truncate text-xs text-[#5C6A87]">
                    {[data.site.address, data.site.postal_code, data.site.city].filter(Boolean).join(" ")}
                  </p>
                  <p className="mt-1 text-[11px] text-[#93A0BC]">
                    {[data.site.building_type_label, data.site.peb_class && `PEB ${data.site.peb_class}`].filter(Boolean).join(" · ")}
                  </p>
                </Link>
                {data.client && (
                  <p className="mt-2 text-xs text-[#5C6A87]">
                    Client : <span className="font-semibold text-[#0F2645]">{data.client.company || data.client.name}</span>
                    {data.client.phone ? ` · ${data.client.phone}` : ""}
                  </p>
                )}
              </SectionCard>
            )}

            <button type="button" data-testid="delete-equipment" onClick={removeEquipment} className="inline-flex min-h-[40px] items-center gap-2 px-1 text-xs font-semibold text-rose-600 hover:underline">
              <Trash2 className="h-3.5 w-3.5" /> Supprimer cet équipement
            </button>
          </div>
        </div>
      )}
    </StudioLayout>
  );
}
